/* Append collection pages on demand; preserve the native page URLs as fallback. */
(() => {
  if (window.smCollectionLoadMore) return;
  window.smCollectionLoadMore = true;
  let request;
  let urls = [];
  let restoring = false;
  let pendingBackState;
  const controls = () => document.querySelector('[data-sm-load-more]');
  const listFor = box => document.getElementById('product-list-' + box.dataset.sectionId);
  const baseURL = () => location.pathname + location.search;
  const cachePrefix = 'sm-collection-list:';
  const mobile = matchMedia('(max-width:699px)');
  const pendingCards = list => [...list.children].filter(card => card.hasAttribute('data-sm-mobile-pending'));
  function refresh(box = controls()) {
    if (!box) return;
    const list = listFor(box);
    if (!list) return;
    const pending = pendingCards(list).length;
    const count = list.children.length - (mobile.matches ? pending : 0);
    box.querySelector('[data-sm-load-count]').textContent = (Number(box.dataset.offset) + count) + ' of ' + box.dataset.total + ' products';
    const next = box.querySelector('[data-sm-load-next]');
    if (next) {
      next.hidden = !box.dataset.nextUrl && !(mobile.matches && pending);
      next.classList.toggle('sm-load-next--local', !box.dataset.nextUrl);
      if (box.dataset.nextUrl) next.href = box.dataset.nextUrl;
    }
  }
  function revealBatch(list, amount) {
    pendingCards(list).slice(0, amount).forEach(card => card.removeAttribute('data-sm-mobile-pending'));
    visibleCards(list);
  }
  mobile.addEventListener('change', () => refresh());
  refresh();
  function visibleCards(list) {
    list.querySelectorAll('product-card[reveal-on-scroll]').forEach(card => {
      card.removeAttribute('reveal-on-scroll');
      card.style.opacity = '1';
      card.style.transform = 'none';
    });
  }
  function save() {
    const box = controls();
    const list = box && listFor(box);
    if (!list || restoring) return;
    const key = history.state?.smCollectionList?.key || Date.now().toString(36) + Math.random().toString(36).slice(2);
    const state = { key, url:baseURL(), urls:urls.length ? urls.slice() : [baseURL()], y:scrollY, sectionId:box.dataset.sectionId, shown:list.children.length - (mobile.matches ? pendingCards(list).length : 0) };
    try {
      // Bound per-tab storage; quota failures fall back to fetching visited pages on Back.
      const entries = Object.keys(sessionStorage).filter(k => k.startsWith(cachePrefix));
      while (entries.length >= 3 && !entries.includes(cachePrefix + key)) sessionStorage.removeItem(entries.shift());
      const value = JSON.stringify({ list:list.innerHTML, controls:box.outerHTML, url:state.url });
      if (value.length < 1800000) sessionStorage.setItem(cachePrefix + key, value);
      else sessionStorage.removeItem(cachePrefix + key);
    } catch (_) { /* Private browsing or storage quota: URLs remain in history state. */ }
    history.replaceState({ ...history.state, smCollectionList:state }, '', location.href);
  }
  async function getPage(url, sectionId, signal) {
    const target = new URL(url, location.href);
    if (target.origin !== location.origin || target.pathname !== location.pathname) throw new Error('Invalid collection page');
    target.searchParams.set('section_id', sectionId);
    const response = await fetch(target, { signal, credentials:'same-origin' });
    if (!response.ok) throw new Error('Collection request failed');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const box = doc.querySelector('[data-sm-load-more]');
    const list = doc.getElementById('product-list-' + sectionId);
    if (!box || !list) throw new Error('Collection response is incomplete');
    // DOMParser treats noscript contents as markup; never activate fallback styles on insertion.
    box.querySelectorAll('noscript').forEach(node => node.remove());
    return { box, list };
  }
  document.addEventListener('click', async event => {
    const next = event.target.closest('[data-sm-load-next]');
    if (!next || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    const box = next.closest('[data-sm-load-more]');
    const list = listFor(box);
    if (!list || next.getAttribute('aria-disabled') === 'true') return;
    const batch = Number(box.dataset.mobileBatch) || 24;
    const pending = pendingCards(list).length;
    if (mobile.matches && pending >= batch) {
      revealBatch(list, batch); refresh(box); save(); return;
    }
    if (!box.dataset.nextUrl) {
      if (mobile.matches) revealBatch(list, batch);
      refresh(box); save(); return;
    }
    const originalURL = baseURL();
    const pageURL = new URL(box.dataset.nextUrl, location.href).href;
    request?.abort();
    const controller = request = new AbortController();
    next.setAttribute('aria-disabled','true');
    next.textContent = 'Loading…';
    box.setAttribute('aria-busy','true');
    box.querySelector('[data-sm-load-error]').hidden = true;
    try {
      const page = await getPage(pageURL, box.dataset.sectionId, controller.signal);
      if (!box.isConnected || originalURL !== baseURL()) return;
      // Cancel stale responses if filters replaced this list while loading.
      if (!urls.length) urls = [originalURL];
      if (mobile.matches) [...page.list.children].forEach(card => card.setAttribute('data-sm-mobile-pending',''));
      visibleCards(page.list);
      list.append(...page.list.children);
      if (mobile.matches) revealBatch(list, batch);
      const shown = Number(box.dataset.offset) + list.children.length;
      page.box.querySelector('[data-sm-load-count]').textContent = shown + ' of ' + page.box.dataset.total + ' products';
      // Keep the starting offset for collections opened directly at page 2 or later.
      page.box.dataset.offset = box.dataset.offset;
      box.replaceWith(page.box);
      urls.push(pageURL);
      refresh(page.box);
      save();
    } catch (error) {
      if (error.name !== 'AbortError' && box.isConnected) {
        const message = box.querySelector('[data-sm-load-error]');
        message.textContent = 'Could not load more products. Please try again.';
        message.hidden = false;
      }
    } finally {
      next.removeAttribute('aria-disabled'); next.textContent = 'Load More'; box.removeAttribute('aria-busy');
    }
  });
  // Save once when leaving a product list; avoid writes on every scroll event.
  document.addEventListener('click', event => {
    if (event.target.closest('product-list a[href]')) save();
  }, true);
  window.addEventListener('pagehide', save);
  document.addEventListener('sm:filters-updated', () => {
    request?.abort(); urls = []; // New filters/sort start a fresh list and discard stale requests.
    if (pendingBackState) { const state = pendingBackState; pendingBackState = null; restore(state); }
    else refresh();
  });
  async function restore(state) {
    const box = controls();
    if (!state || !box || state.url !== baseURL()) return;
    restoring = true;
    request?.abort();
    const controller = request = new AbortController();
    try {
      let cached;
      try { cached = JSON.parse(sessionStorage.getItem(cachePrefix + state.key)); } catch (_) {}
      const list = listFor(box);
      const initialPageOnly=(state.urls||[state.url]).length===1;
      // A normal Back navigation already supplies the initial product list.
      // Replacing it with serialized HTML destroys newly initialized previews
      // (canvas pixels are not part of innerHTML) and initializes them twice.
      const reuseInitial=initialPageOnly&&list.children.length>0&&
        list.children.length>=(state.shown||0);
      if (reuseInitial) {
        if(mobile.matches)[...list.children].forEach((card,index)=>{
          card.toggleAttribute('data-sm-mobile-pending',index>=(state.shown||list.children.length));
        });
      } else if (cached?.url === state.url) {
        list.innerHTML = cached.list;
        const template = document.createElement('template');
        template.innerHTML = cached.controls;
        template.content.querySelectorAll('noscript').forEach(node => node.remove());
        box.replaceWith(template.content.firstElementChild);
      } else {
        const pages = state.urls || [state.url];
        let finalBox;
        list.replaceChildren();
        for (const url of pages) {
          const page = await getPage(url, state.sectionId, controller.signal);
          list.append(...page.list.children); finalBox = page.box;
        }
        finalBox.dataset.offset = box.dataset.offset;
        finalBox.querySelector('[data-sm-load-count]').textContent = (Number(box.dataset.offset) + list.children.length) + ' of ' + finalBox.dataset.total + ' products';
        box.replaceWith(finalBox);
      }
      visibleCards(list);
      if (cached?.url !== state.url && mobile.matches) [...list.children].forEach((card,index) => {
        if (index < (state.shown || 24)) card.removeAttribute('data-sm-mobile-pending');
        else card.setAttribute('data-sm-mobile-pending','');
      });
      refresh();
      urls = state.urls || [state.url];
      requestAnimationFrame(() => requestAnimationFrame(() => scrollTo({ top:state.y, behavior:'instant' })));
    } catch (error) {
      if (error.name !== 'AbortError') location.reload();
    } finally { restoring = false; }
  }
  window.addEventListener('popstate', event => { pendingBackState = event.state?.smCollectionList; });
  window.addEventListener('pageshow', event => {
    if (!event.persisted && performance.getEntriesByType('navigation')[0]?.type === 'back_forward') restore(history.state?.smCollectionList);
  });
})();
